/**
 * RoyaleLobbyRoster
 *
 * The waiting-room body once a lobby exists: live roster (scrolls for up to
 * 100), a 15-min expiry countdown, and the host's Start control. Start is
 * enabled at MIN_TO_START joined players and auto-fires at AUTO_START_AT (the
 * display cap) — the parent owns the actual start dispatch; this is pure UI +
 * the enable/auto-fire signalling.
 *
 * Presentational: the parent passes players, whether I'm the host, the
 * expiry timestamp, and callbacks. No store/socket access here.
 */

import React, {useEffect, useMemo} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';

import {
  AUTO_START_AT,
  MIN_TO_START,
} from '@/Screens/FunChallenges/royaleConstants';
import type {RoyaleLobbyPlayer} from '@/Screens/FunChallenges/royaleTypes';
import {buildColorMap} from '@/Screens/FunChallenges/util/royaleColors';

const TEAL_DARK = '#1F6C63';
const GREEN = '#1ECF52';
const TEXT_DARK = '#262322';
const TEXT_MID = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const YELLOW = '#FACF47';
const YELLOW_DARK = '#A9873B';
const BORDER = 'rgba(38, 35, 34, 0.10)';

interface Props {
  players: RoyaleLobbyPlayer[];
  myUserId: string;
  isHost: boolean;
  /** Epoch ms the lobby expires. */
  expiresAt: number;
  /** Now in ms (parent ticks this so the countdown re-renders). */
  nowMs: number;
  /** Host pressed Start. */
  onStart: () => void;
  /** Start has been dispatched; disable the button until the server responds. */
  starting?: boolean;
  /**
   * Fired ONCE when the roster reaches AUTO_START_AT (parent gates classroom
   * mode, where auto-start is disabled, before dispatching).
   */
  onAutoStartReady?: () => void;
}

export default function RoyaleLobbyRoster({
  players,
  myUserId,
  isHost,
  expiresAt,
  nowMs,
  onStart,
  starting = false,
  onAutoStartReady,
}: Props) {
  const count = players.length;
  // Can't start below the minimum, after expiry, or once a start is already
  // in flight (item 5: a 2nd click before the ACK could double-fire start).
  const canStart = count >= MIN_TO_START && !starting;
  // Colour by join order (the roster IS join order), "you" forced green.
  const colorOf = useMemo(
    () => buildColorMap(players.map(p => String(p.userId)), myUserId),
    [players, myUserId],
  );
  const remainingMs = Math.max(0, expiresAt - nowMs);
  const expired = remainingMs <= 0;

  const mmss = useMemo(() => {
    const s = Math.ceil(remainingMs / 1000);
    const mm = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [remainingMs]);

  // Auto-start signal — fire once when we cross the cap.
  useEffect(() => {
    if (count >= AUTO_START_AT && onAutoStartReady) {
      onAutoStartReady();
    }
  }, [count, onAutoStartReady]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <CCText style={styles.headerTitle}>Players · {count}</CCText>
        <View
          style={[
            styles.timerPill,
            remainingMs < 60_000 && styles.timerPillUrgent,
          ]}>
          <CCText
            style={[
              styles.timerText,
              remainingMs < 60_000 && styles.timerTextUrgent,
            ]}>
            {expired ? 'Lobby expired' : `Expires in ${mmss}`}
          </CCText>
        </View>
      </View>

      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}>
        {players.map(p => (
          <View key={p.userId} style={styles.playerRow}>
            <View
              style={[styles.dot, {backgroundColor: colorOf(p.userId)}]}>
              <CCText style={styles.dotText}>
                {(p.name || '?').charAt(0).toUpperCase()}
              </CCText>
            </View>
            <CCText
              style={[
                styles.playerName,
                p.userId === myUserId && styles.playerNameMe,
              ]}
              numberOfLines={1}>
              {p.userId === myUserId ? 'You' : p.name || 'Player'}
            </CCText>
            {p.isHost && (
              <View style={styles.hostBadge}>
                <CCText style={styles.hostBadgeText}>HOST</CCText>
              </View>
            )}
            <View style={styles.onlineDot} />
          </View>
        ))}
      </ScrollView>

      {isHost ? (
        <Pressable
          onPress={canStart && !expired ? onStart : undefined}
          disabled={!canStart || expired}
          style={({pressed}) => [
            styles.startBtn,
            (!canStart || expired) && styles.startBtnDisabled,
            canStart && !expired && pressed && styles.startBtnPressed,
          ]}>
          <CCText
            style={[
              styles.startBtnText,
              (!canStart || expired) && styles.startBtnTextDisabled,
            ]}>
            {expired
              ? 'Lobby expired'
              : starting
              ? 'Starting…'
              : canStart
              ? `Start Royale · ${count} player${count === 1 ? '' : 's'}`
              : `Need ${Math.max(0, MIN_TO_START - count)} more to start`}
          </CCText>
        </Pressable>
      ) : (
        <View style={styles.waitingBox}>
          <CCText style={styles.waitingText}>
            {expired
              ? 'Lobby expired — ask the host to create a new one.'
              : 'Waiting for the host to start…'}
          </CCText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {gap: vs(12)},
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MID,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  timerPill: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
  },
  timerPillUrgent: {backgroundColor: 'rgba(225, 120, 70, 0.14)'},
  timerText: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: TEAL_DARK,
  },
  timerTextUrgent: {color: '#E17846'},
  list: {maxHeight: 360},
  listContent: {gap: vs(8)},
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
  },
  dot: {
    width: 30,
    height: 30,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotText: {color: CCColors.White, fontWeight: '800', fontSize: 12},
  playerName: {
    flex: 1,
    fontSize: FontSizes[14] || 14,
    fontWeight: '600',
    color: TEXT_DARK,
  },
  playerNameMe: {color: TEAL_DARK, fontWeight: '800'},
  hostBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 5,
    backgroundColor: 'rgba(250, 207, 71, 0.22)',
  },
  hostBadgeText: {
    fontSize: 9,
    fontWeight: '800',
    color: YELLOW_DARK,
    letterSpacing: 0.5,
  },
  onlineDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: GREEN,
  },
  startBtn: {
    marginTop: vs(4),
    backgroundColor: YELLOW,
    borderRadius: 14,
    borderBottomWidth: 4,
    borderColor: YELLOW_DARK,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  startBtnPressed: {borderBottomWidth: 2, paddingVertical: 18},
  startBtnDisabled: {
    backgroundColor: '#F2E8C7',
    borderColor: '#D9C796',
  },
  startBtnText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  startBtnTextDisabled: {color: TEXT_MUTED},
  waitingBox: {
    marginTop: vs(4),
    padding: 14,
    borderRadius: 12,
    backgroundColor: 'rgba(61, 171, 158, 0.10)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.25)',
  },
  waitingText: {
    fontSize: FontSizes[13] || 13,
    color: TEAL_DARK,
    fontWeight: '600',
    textAlign: 'center',
  },
});
